import {
    Token,
    GetUserResponse,
    OverwolfApi,
} from "@devour/client";
import { SendMessageToOW } from "./useOverwolfInterop";
import getConfig from "@/utils/getConfig";

/**
 * Original function to emit full token and full user data. Remove once limited token OPK is fully deployed on overwolf.
 * @param token
 * @param userData
 * @param sendMessageToOW
 * @param isFreshLogin
 */
export const waitForOverwolfAuthHandshakeOG = (token: Token, userData: GetUserResponse, sendMessageToOW: SendMessageToOW, isFreshLogin: boolean = false) => {
    /*
     * This handshake will ensure that the OW app has recieved / set the user data
     * before devourgo requests info about the running game.
     * (i.e. auth data MUST be set before de:ready is sent form devourgo)
     */

    /*
     * Initially wanted to do setup the handshake as a promise, where the listener
     * would resolve the promise, but settled on this to avoid race conditions where we
     * send de:data before the handshake listener is setup.
     */

    // Check if we're actually in Overwolf environment
    const isInOverwolfEnvironment = window.sessionStorage.getItem("isOnOverwolf") === "true";

    // In development or when not in Overwolf, skip the handshake to prevent render loops
    if (!isInOverwolfEnvironment) {
        if (import.meta.env.DEV) {
            console.log("[Handshake] Not in Overwolf environment, skipping handshake");
        }
        /*
         * Send data once and mark as ready immediately. The payload shape on the wire
         * predates the limited-token migration — DevourMessage.payload.token is typed
         * `string` but legacy Overwolf builds expect the full Token object. The
         * ts-expect-error covers the boundary until those builds roll past v0.1.X.
         */
        // @ts-expect-error legacy Overwolf payload — see comment above
        sendMessageToOW({type: "de:data", payload: {token: token, userData, isLogin: isFreshLogin}, isLogin: isFreshLogin});
        sendMessageToOW({type: "de:ready"});
        return;
    }

    let handshakeRecieved = false;
    const handshakeListener = (event: MessageEvent) => {
        if (event.data?.payload?.type === "ow:login-handshake") {
            handshakeRecieved = true;
            window.removeEventListener("message", handshakeListener);
        }
    };
    window.addEventListener("message", handshakeListener);
    /*
     * Send data immediately once. Same legacy-payload suppression as the
     * early-return branch above — once Overwolf builds roll past v0.1.X this
     * file collapses with `waitForOverwolfAuthHandshake` proper.
     */
    // @ts-expect-error legacy Overwolf payload — see comment above
    sendMessageToOW({type: "de:data", payload: {token: token, userData, isLogin: isFreshLogin}, isLogin: isFreshLogin});

    // Will resend data at longer intervals until handshake is received or 2 seconds have passed
    const dataInterval = setInterval(() => {
        // @ts-expect-error legacy Overwolf payload — see comment above
        sendMessageToOW({type: "de:data", payload: {token: token, userData, isLogin: isFreshLogin}, isLogin: isFreshLogin});
    }, 500); // Changed from 100ms to 500ms to reduce message spam
    const killIntervalAt = new Date().getTime() + 2000;
    const intervalId = setInterval(() => {
        if (handshakeRecieved) {
            sendMessageToOW({type: "de:ready"});
            clearInterval(dataInterval);
            clearInterval(intervalId);
        } else if (new Date().getTime() > killIntervalAt) {
            clearInterval(intervalId);
            clearInterval(dataInterval);
            sendMessageToOW({type: "de:ready"});
            window.removeEventListener("message", handshakeListener);
        }
    }, 100);
};

/**
 * Function to emit with reduced token permissions. Only emits user nickname and gravatar to external windows.
 * @param fullToken
 * @param userData
 * @param sendMessageToOW
 * @param isFreshLogin
 */
const waitForOverwolfAuthHandshakeLimited = async (fullToken: Token, userData: GetUserResponse, sendMessageToOW: SendMessageToOW, publicKey: string, isFreshLogin: boolean = false) => {
    /*
     * This handshake will ensure that the OW app has recieved / set the user data
     * before devourgo requests info about the running game.
     * (i.e. auth data MUST be set before de:ready is sent form devourgo)
     */

    /*
     * Initially wanted to do setup the handshake as a promise, where the listener
     * would resolve the promise, but settled on this to avoid race conditions where we
     * send de:data before the handshake listener is setup.
     */
    /*
     * publicKey is resolved by the caller from the URL OR sessionStorage — re-
     * reading only the URL here dropped the sessionStorage case, sending id:null.
     */
    const generateEventSubmissionToken = await new OverwolfApi(getConfig(fullToken)).createOverwolfEventSubmissionTokenId({
        id: publicKey,
    });
    const oAuthToken = generateEventSubmissionToken.oAuthToken;

    // Check if we're actually in Overwolf environment
    const isInOverwolfEnvironment = window.sessionStorage.getItem("isOnOverwolf") === "true";

    // In development or when not in Overwolf, skip the handshake to prevent render loops
    if (!isInOverwolfEnvironment) {
        if (import.meta.env.DEV) {
            console.log("[Handshake] Not in Overwolf environment, skipping handshake");
        }
        // Send data once and mark as ready immediately
        sendMessageToOW({
            type: "de:data",
            payload: {
                token: oAuthToken,
                userData: {
                    id: userData.user.id,
                    nickname: userData.user.nickname,
                    gravatar: userData.user.gravatar,
                    xpMultipliers: userData.user.xpMultipliers,
                },
                isLogin: isFreshLogin,
            },
            isLogin: isFreshLogin,
        });
        sendMessageToOW({type: "de:ready"});
        return;
    }

    let handshakeRecieved = false;
    const handshakeListener = (event: MessageEvent) => {
        if (event.data?.payload?.type === "ow:login-handshake") {
            handshakeRecieved = true;
            window.removeEventListener("message", handshakeListener);
        }
    };
    window.addEventListener("message", handshakeListener);
    // Send data immediately once
    sendMessageToOW({
        type: "de:data",
        payload: {
            token: oAuthToken,
            userData: {
                id: userData.user.id,
                nickname: userData.user.nickname,
                gravatar: userData.user.gravatar,
                xpMultipliers: userData.user.xpMultipliers,
            },
            isLogin: isFreshLogin,
        },
        isLogin: isFreshLogin,
    });

    // Will resend data at longer intervals until handshake is received or 2 seconds have passed
    const dataInterval = setInterval(() => {
        sendMessageToOW({
            type: "de:data",
            payload: {
                token: oAuthToken,
                userData: {
                    id: userData.user.id,
                    nickname: userData.user.nickname,
                    gravatar: userData.user.gravatar,
                    xpMultipliers: userData.user.xpMultipliers,
                },
                isLogin: isFreshLogin,
            },
            isLogin: isFreshLogin,
        });
    }, 500); // Changed from 100ms to 500ms to reduce message spam
    const killIntervalAt = new Date().getTime() + 2000;
    const intervalId = setInterval(() => {
        if (handshakeRecieved) {
            sendMessageToOW({type: "de:ready"});
            clearInterval(dataInterval);
            clearInterval(intervalId);
        } else if (new Date().getTime() > killIntervalAt) {
            clearInterval(intervalId);
            clearInterval(dataInterval);
            sendMessageToOW({type: "de:ready"});
            window.removeEventListener("message", handshakeListener);
        }
    }, 100);
};

export const waitForOverwolfAuthHandshake = async (fullToken: Token, userData: GetUserResponse, sendMessageToOW: SendMessageToOW, isFreshLogin: boolean = false) => {
    // If public key is passed then overwolf opk has updated to accept limited token.
    const urlParams = new URLSearchParams(window.location.search);
    const publicKey = urlParams.get("publicKey") || window.sessionStorage.getItem("publicKey");
    if (publicKey) {
        await waitForOverwolfAuthHandshakeLimited(fullToken, userData, sendMessageToOW, publicKey, isFreshLogin);
    } else {
        await waitForOverwolfAuthHandshakeOG(fullToken, userData, sendMessageToOW, isFreshLogin);
    }
};